<template>
  <router-view />
  <Toast />
  <ConfirmDialog
    :style="{ width: '32rem' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
  />
</template>

<script setup>
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePrimeVue } from 'primevue/config'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import { buildPrimeLocale } from '@/i18n/primevueLocale'
import { useThemeStore } from '@/stores/theme'
import { useDarkMode } from '@/composables/useDarkMode'
import { useSiteSettingsStore } from '@/stores/siteSettings'

const { t, locale } = useI18n()
const primevue = usePrimeVue()

// Keep PrimeVue's built-in strings (aria labels, pagination, date picker...) in the app language
watch(locale, (lang) => {
  Object.assign(primevue.config.locale, buildPrimeLocale(lang))
  primevue.config.locale.accept = t('common.yes')
  primevue.config.locale.reject = t('common.no')
}, { immediate: true })

const themeStore = useThemeStore()
const siteSettingsStore = useSiteSettingsStore()
const { init: initDarkMode } = useDarkMode()

// index.html only carries the built-in favicon: swap in the one set in
// Cài đặt trang → Thương hiệu (and follow later changes made there).
watch(() => siteSettingsStore.branding.favicon_url, (url) => {
  const link = document.querySelector('link#favicon')
  const href = url || '/brand/favicon.png'
  if (!link || link.getAttribute('href') === href) return
  link.removeAttribute('type') // uploads may be .ico/.svg/.jpg, not only PNG
  link.setAttribute('href', href)
})
// Loading screens (the splash in index.html, table loading masks) show the uploaded logo instead of the
// built-in loading image. The address is remembered so the next page open can show it before any API call.
function rememberLogo(url) {
  try {
    if (url) {
      localStorage.setItem('sf.logo', url)
      document.documentElement.style.setProperty('--sf-loading-img', `url("${url.replace(/"/g, '%22')}")`)
    } else {
      localStorage.removeItem('sf.logo')
      document.documentElement.style.removeProperty('--sf-loading-img')
    }
  } catch { /* storage unavailable: the default loading image stays */ }
}
watch(() => siteSettingsStore.branding.logo_url, rememberLogo)
// Name under the iOS home-screen icon (Android takes it from the API's manifest)
watch(() => siteSettingsStore.branding.site_name, (name) => {
  document.querySelector('meta#app-title')?.setAttribute('content', name || 'SmartFarm')
})

onMounted(async () => {
  // Apply dark/light class before anything renders to avoid a flash
  initDarkMode()
  // Paint with the built-in defaults immediately, no flash...
  themeStore.loadTheme()
  // ...then swap in the farm's actual brand colors once loaded (admin/roles →
  // Cài đặt trang → Thương hiệu is what writes these).
  await siteSettingsStore.load()
  rememberLogo(siteSettingsStore.branding.logo_url) // also clears a remembered logo the admin has since removed
  themeStore.setColors({
    primary_color: siteSettingsStore.branding.primary_color,
    secondary_color: siteSettingsStore.branding.secondary_color
  })
})
</script>

<style>
/* Theme CSS variables with defaults — overridden by stores/theme.js at runtime */
:root {
  --theme-primary: #16a34a;
  --theme-secondary: #0f3d24;
  --theme-primary-rgb: 22, 163, 74;
  --theme-secondary-rgb: 15, 61, 36;

  --theme-primary-hover: #128a3e;
  --theme-primary-active: #107536;
  --theme-primary-light: #cdecd8;
  --theme-primary-lighter: #e8f7ed;
  --theme-primary-dark: #0e5c2c;

  --theme-secondary-hover: #295c40;
  --theme-secondary-active: #3d7a55;
  --theme-secondary-light: #d3e5da;
  --theme-secondary-lighter: #ecf3ef;
}

.router-link-active.router-link-exact-active.menu-link {
  background-color: var(--theme-primary);
  color: #fff;
}

.menu-link.router-link-active.router-link-exact-active :is(.menu-icon) {
  color: #fff !important;
}

.menu-link {
  transition: all .3s cubic-bezier(.56, .55, .56, .55);
}
</style>
